import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { Copy, Loader2, MapPin, MessageSquare, Send, X } from "lucide-react";
import { HintResolver } from "@/components/HintResolver";
import { CallerPhotos } from "@/components/CallerPhotos";
import { messageBeeps } from "@/lib/audio";

const NUDGE_AFTER_MS = 60000;

const CANNED = [
  "Help is on the way. Stay on this page.",
  "Can you get somewhere safe?",
  "What do you see around you — signs, stores, landmarks?",
  "Are you hurt? Reply YES or NO.",
  "Is the suspect still there?",
];

// Intake-time Text-to-Locate: caller doesn't know where they are → issue link now, GPS fills the address field.
export const IntakeLocate = ({ phone, onFix, onToken, disabled = false, prominent = false }) => {
  const [sess, setSess] = useState(null);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [nudge, setNudge] = useState(null);
  const appliedRef = useRef(null);
  const poll = () => sess && client.get(`/locate/pre/${sess.token}`).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { if (!sess) return; poll(); const iv = setInterval(poll, 4000); return () => clearInterval(iv); }, [sess]); // eslint-disable-line
  useRealtime(["locate_pre"], () => poll());
  // Caller Location Nudge — no share within 60 s → auto re-send the link (SMS when configured) and alert the dispatcher.
  useEffect(() => {
    if (!sess || st?.fix) return;
    const h = setTimeout(async () => {
      try {
        const { data } = await client.post(`/locate/nudge/${sess.token}`);
        if (data.skipped) return;
        setNudge(data);
        messageBeeps();
        toast.warning(data.sms?.sent ? `No location yet — link re-texted to caller (nudge ${data.nudges}/${data.max})` : `No location after 60 s — re-read the link to the caller or try the landmark fallback (nudge ${data.nudges}/${data.max})`, { duration: 8000 });
      } catch { /* noop */ }
    }, NUDGE_AFTER_MS);
    return () => clearTimeout(h);
  }, [sess, st?.fix, nudge?.nudges]); // eslint-disable-line
  useEffect(() => {
    if (st?.fix && st.fix.ts !== appliedRef.current) {
      appliedRef.current = st.fix.ts;
      onFix?.(st.fix, st.address);
      toast.success(`Caller GPS received ±${Math.round(st.fix.accuracy_m || 0)} m — address filled from caller's phone`);
    }
  }, [st?.fix?.ts, st?.address]); // eslint-disable-line
  const applyPick = (fix, address) => { appliedRef.current = fix.ts; onFix?.(fix, address); };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/locate/pre", { phone: phone || null });
      setSess(data); onToken?.(data.token);
      toast.success(data.sms?.sent ? `Locate link texted to ${data.sms.to}` : "Locate link ready — read or send it to the caller");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start locate"); }
    finally { setBusy(false); }
  };
  const copy = (s) => navigator.clipboard?.writeText(s).then(() => toast.success("Copied"));
  const reply = async (body = text) => {
    if (!body.trim()) return;
    try { await client.post(`/locate/pre/${sess.token}/chat`, { text: body }); setText(""); poll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reply failed"); }
  };

  if (!sess) {
    return (
      <div data-testid="intake-locate-prompt" className={`flex items-center gap-3 flex-wrap ${prominent ? "border border-[#F59E0B]/70 bg-[#F59E0B]/10 px-3 py-2 rounded-sm" : ""}`}>
        {prominent && <span className="font-mono text-[11px] text-[#FCD34D]"><b>Caller doesn't know where they are?</b> Send a locate link — their phone's GPS fills this address in seconds. If GPS fails, they can text a landmark or photo.</span>}
        <button type="button" data-testid="intake-locate-btn" onClick={start} disabled={busy || disabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-head font-bold border transition-colors disabled:opacity-50 ${prominent ? "bg-[#DC2626] border-[#DC2626] text-white hover:bg-[#b91c1c]" : "border-[#DC2626]/60 text-[#FCA5A5] hover:bg-[#DC2626]/15"}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />} {prominent ? "LOCATE CALLER" : "Locate Caller — caller unsure of location"}
        </button>
      </div>
    );
  }
  const fix = st?.fix;
  return (
    <div data-testid="intake-locate-panel" className="border border-[#DC2626]/50 bg-[#1a0d0d] p-2.5 rounded-sm space-y-2">
      <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-wider text-[#FCA5A5]">
        <span><MapPin size={10} className="inline mr-1" />Text-to-Locate (pre-call) · {sess.sms?.sent ? `texted to ${sess.sms.to}` : "read / send link to caller"} · expires {sess.expires_at.slice(11, 16)}Z</span>
        <button type="button" onClick={() => { setSess(null); setSt(null); onToken?.(null); }} title="Discard session"><X size={11} /></button>
      </div>
      <div className="flex gap-2 items-start">
        <img src={sess.qr} alt="QR" className="w-16 h-16 bg-white p-0.5 rounded-sm shrink-0" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex gap-1 items-center">
            <code data-testid="intake-locate-url" className="font-mono text-[10px] text-white truncate flex-1">{sess.url}</code>
            <button type="button" data-testid="intake-locate-copy" onClick={() => copy(sess.url)} className="px-1.5 py-0.5 text-[9px] border border-[#2C2D35] text-[#9CA3AF] hover:text-white flex items-center gap-1"><Copy size={9} /> URL</button>
            <button type="button" onClick={() => copy(sess.sms_text)} className="px-1.5 py-0.5 text-[9px] border border-[#2C2D35] text-[#9CA3AF] hover:text-white flex items-center gap-1"><Copy size={9} /> SMS</button>
          </div>
          <div className="font-mono text-[10px] text-[#9CA3AF]">Say: "I'm sending you a link — tap it and allow location so I can see exactly where you are."</div>
          {fix
            ? <div data-testid="intake-locate-fix" className="font-mono text-[11px] text-[#6EE7B7]">✓ CALLER GPS {fix.lat.toFixed(5)}, {fix.lng.toFixed(5)}{fix.accuracy_m ? ` ±${Math.round(fix.accuracy_m)} m` : ""} · {new Date(fix.ts).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 8)}{st.address ? ` → ${st.address}` : ""}<div className="text-[9px] text-[#9CA3AF]">Address field filled from caller's phone — edit if needed. Fixes & chat carry onto the call when you create it.</div></div>
            : <div className="font-mono text-[10px] text-[#F59E0B] flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Waiting for caller to tap the link and share location…{st ? ` (${st.shares} shares)` : ""}{(st?.nudges || nudge?.nudges) ? <span data-testid="intake-nudge-count" className="text-[#FCA5A5]"> · nudged ×{st?.nudges || nudge?.nudges}</span> : null}</div>}
        </div>
      </div>
      {(!fix || st?.hint) && <HintResolver hint={st?.hint} onPick={(c) => applyPick({ lat: c.lat, lng: c.lng, accuracy_m: null, ts: new Date().toISOString(), source: c.source }, c.address)} />}
      <CallerPhotos photos={st?.photos || []} compact onPick={(c) => applyPick({ lat: c.lat, lng: c.lng, accuracy_m: null, ts: new Date().toISOString(), source: "photo_vision" }, c.address)} />
      <div className="border-t border-[#2C2D35] pt-1.5 space-y-1">
        <div className="font-mono text-[9px] uppercase text-[#93C5FD] flex items-center gap-1"><MessageSquare size={10} /> Text chat with caller</div>
        <div className="max-h-24 overflow-y-auto space-y-0.5">
          {(st?.messages || []).map((m, i) => (
            <div key={i} data-testid={`intake-chat-${m.dir}`} className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm max-w-[90%] ${m.dir === "in" ? "bg-[#1E3A8A] text-white" : "bg-[#1F2937] text-[#D1D5DB] ml-auto"}`}>
              <span className="text-[8px] uppercase opacity-60 mr-1">{m.dir === "in" ? "Caller" : m.from}</span>{m.text}
            </div>))}
        </div>
        <div className="flex gap-1 flex-wrap">
          {CANNED.map((c, i) => <button type="button" key={i} data-testid={`intake-canned-${i}`} onClick={() => reply(c)} className="px-1.5 py-0.5 text-[9px] font-mono border border-[#3B82F6]/60 text-[#BFDBFE] rounded-sm hover:bg-[#3B82F6]/30 truncate max-w-full">{c}</button>)}
        </div>
        <div className="flex gap-1.5">
          <input data-testid="intake-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); reply(); } }} placeholder="Ask the caller where they are…"
            className="flex-1 min-w-0 bg-[#141518] border border-[#2C2D35] text-white px-2 py-1 text-[11px] font-mono outline-none" />
          <button type="button" data-testid="intake-chat-send" onClick={() => reply()} className="px-2 border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/20"><Send size={11} /></button>
        </div>
      </div>
    </div>
  );
};
